export interface OrderData {
  productName: string
  productPrice: number
  quantity: number
  total: number
  currency?: string
}

export interface CustomerInfo {
  firstName: string
  lastName: string
  email: string
  phone?: string
  shipping: {
    address_line_1: string
    address_line_2?: string
    city: string
    state: string
    postal_code: string
    country: string
  }
}

export const paymentService = {
  /**
   * Create a Stripe Checkout Session (hosted checkout page)
   */
  async createCheckoutSession(orderData: OrderData, customerInfo: CustomerInfo) {
    try {
      // Transform orderData into the format the API expects
      const items = [{
        name: orderData.productName,
        price: orderData.productPrice / 100, // Convert from cents to dollars
        quantity: orderData.quantity,
        image: 'https://example.com/midnight-mix-vol-1.jpg' // Add product image URL
      }];

      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items, // Send items array instead of orderData
          customerInfo,
        }),
      });

      // Parse response
      const contentType = response.headers.get('content-type');
      const isJson = contentType?.includes('application/json');

      let data;
      let errorDetails;

      if (isJson) {
        data = await response.json();
        errorDetails = data;
      } else {
        // Handle non-JSON responses (HTML error pages)
        const htmlText = await response.text();
        errorDetails = {
          error: 'Server returned non-JSON response',
          statusCode: response.status,
          responsePreview: htmlText.substring(0, 200)
        };
      }

      // Handle error responses
      if (!response.ok) {
        const errorMessage = errorDetails.error || 'Payment system error';
        const troubleshooting = errorDetails.troubleshooting || '';
        const details = errorDetails.details || '';

        let userMessage = errorMessage;
        
        if (response.status === 500) {
          userMessage = '🔧 Payment System Configuration Issue\n\n';
          userMessage += errorMessage + '\n\n';
          if (details) userMessage += details + '\n\n';
          if (troubleshooting) userMessage += '💡 ' + troubleshooting;
        } else if (response.status === 400) {
          userMessage = '❌ Payment Request Error\n\n' + errorMessage;
          if (errorDetails.validationErrors) {
            userMessage += '\n\nPlease fix:\n• ' + errorDetails.validationErrors.join('\n• ');
          }
        } else if (response.status === 401 || response.status === 403) {
          userMessage = '🔐 Stripe Authentication Issue\n\n' + errorMessage;
          if (troubleshooting) userMessage += '\n\n💡 ' + troubleshooting;
        } else {
          userMessage = errorMessage;
          if (details) userMessage += '\n\n' + details;
        }

        throw new Error(userMessage);
      }

      // Validate successful response - check for url property
      if (!data?.url) {
        throw new Error('Invalid checkout response - missing checkout URL');
      }

      return data;
    } catch (error: any) {
      // Network/fetch errors
      if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
        throw new Error(
          '🌐 Network Connection Error\n\n' + 'Unable to reach the payment system. This could be due to:\n'+ '• Internet connectivity issues\n'+ '• Payment API server is down\n'+ '• Firewall blocking the connection\n\n'+ '💡 Please check your internet connection and try again.'
        );
      }

      // Re-throw formatted errors
      throw error;
    }
  },

  /**
   * Format amount for display
   */
  formatAmount(amount: number, currency: string = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100)
  }
}